/**
 * Device ID & Browser Fingerprint utility
 * - Device ID: random UUID stored in localStorage (clearable)
 * - Fingerprint: hardware/browser fingerprint via FingerprintJS (persists across incognito & storage clears)
 */

const DEVICE_ID_KEY = 'ss_device_id';
const FINGERPRINT_KEY = 'ss_fingerprint';

export function getDeviceId() {
    let deviceId = localStorage.getItem(DEVICE_ID_KEY);
    if (!deviceId) {
        deviceId = crypto.randomUUID();
        localStorage.setItem(DEVICE_ID_KEY, deviceId);
    }
    return deviceId;
}

// Lazy-loaded FingerprintJS promise (singleton)
let _fpPromise = null;

function getFpAgent() {
    if (!_fpPromise) {
        _fpPromise = import('https://openfpcdn.io/fingerprintjs/v5')
            .catch(() => import('https://cdn.jsdelivr.net/npm/@fingerprintjs/fingerprintjs@5/dist/fp.esm.min.js'))
            .then(FingerprintJS => FingerprintJS.load());
    }
    return _fpPromise;
}

/**
 * Get the browser fingerprint (visitorId).
 * Returns cached value if available, otherwise generates a new one.
 * @returns {Promise<string|null>} The fingerprint string, or null on failure
 */
export async function getFingerprint() {
    // Return cached value for sync-speed access
    const cached = localStorage.getItem(FINGERPRINT_KEY);
    if (cached) return cached;

    try {
        const fp = await getFpAgent();
        const result = await fp.get();
        const visitorId = result.visitorId;
        localStorage.setItem(FINGERPRINT_KEY, visitorId);
        return visitorId;
    } catch (err) {
        console.warn('Fingerprint generation failed:', err);
        return null;
    }
}

/**
 * Initialize fingerprint in the background (call early at app startup).
 * This pre-loads the library and caches the fingerprint for later use.
 */
export function initFingerprint() {
    getFingerprint().catch(() => {});
}
